import { FunctionComponent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { FeatureTileEditorDialogContent } from "./FeatureTileEditorDialogContent/FeatureTileEditorDialogContent";
import {
  createInitialFeatureTileEditorDialogState,
  IFeatureTileEditorDialogState,
} from "./featureTileEditorDialogTypes";

import "./FeatureTileEditorDialog.scss";

import { FeatureTileDto } from "../../../../../../@api";

import { sharedDialog } from "../../../../Shared/Components/Dialogs/SharedDialog";
import { featureTileEditorDialogFooterActions } from "./featureTileEditorDialogFooterActions";

interface IFeatureTileEditorDialogProps {
  tile: FeatureTileDto | null;
  defaultOrder: number;
  isVisible: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (tile: FeatureTileDto) => Promise<void>;
}

const featureKeys = translationKeys.adminArea.featureTiles;

export const FeatureTileEditorDialog: FunctionComponent<IFeatureTileEditorDialogProps> = ({
  tile,
  defaultOrder,
  isSaving,
  isVisible,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    sharedDialog.openSharedDialog({
      title: tile ? t(featureKeys.actions.update.name) : t(featureKeys.actions.create.name),
      onClose: () => onCancel(),
      size: "large",
      sharedState: {
        ...createInitialFeatureTileEditorDialogState(tile, defaultOrder),
        isSaving,
      } satisfies IFeatureTileEditorDialogState,
      customContent: <FeatureTileEditorDialogContent disabled={isSaving} />,
      footerActions: featureTileEditorDialogFooterActions({
        isEdit: Boolean(tile),
        disabled: isSaving,
        defaultOrder,
        onCancel: () => onCancel(),
        onSave: async (tileToSave: FeatureTileDto) => onSaveRef.current(tileToSave),
      }),
      className: "feature-tile-editor-dialog",
      customDismissBehavior: { closeIcon: true, escape: false, outsideClick: false },
    });
  }, [isVisible]);

  return null;
};
